import {HttpErrorResponse} from '@angular/common/http';
import {Component, HostListener, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {FabTableState, filterStateChange, process} from '@fabric/components/table-filter/data-query';
import {catchError, forkJoin, map, Observable, ObservableInput, of, tap} from 'rxjs';
import {ErrorCodes} from '../../constants/ErrorCodes';
import {UserFunctionCode} from '../../constants/UserFunctionCode';
import {DeactivateGuardedComponent} from '../../interfaces/DeactivateGuardedComponent';
import {Cai} from '../../models/Cai';
import {PtoEmision} from '../../models/PtoEmision';
import {CaiService} from '../../services/cai.service';
import {CommonService} from '../../services/common.service';
import {NotificationService} from '../../services/notification.service';
import {PtoEmisionService} from '../../services/ptoEmision.service';
import {SecurityService} from '../../services/security.service';
import {
  FabDateFilterOperator,
  RemDateFilterComponent
} from '../../shared-components/table-filter/date-filter/date-filter.component';
import {RemNumberFilterComponent} from '../../shared-components/table-filter/number-filter/number-filter.component';
import {
  FabStringFilterOperator,
  RemStringFilterComponent
} from '../../shared-components/table-filter/string-filter/string-filter.component';

class CaiRow extends Cai {
  public add: boolean;
  public editing: boolean = false;

  private _originalCai: Cai;

  public set originalCai(cai: Cai) {
    this._originalCai = cai;
    this.resetEdits();
  }

  public get originalCai(): Cai {
    return this._originalCai;
  }

  constructor(add: boolean = false, cai?: Cai) {
    super();
    this._originalCai = cai ?? new Cai();
    this.add = add;
    this.editing = add;
    this.resetEdits();
  }

  resetEdits() {
    const keys = Object.keys(new Cai()) as (keyof Cai)[];
    keys.forEach((key) => {
      switch (key) {
        case 'fecDesdeCai':
          this.fecDesdeCai = new Date(this._originalCai.fecDesdeCai);
          break;
        case 'fecVtoCai':
          this.fecVtoCai = new Date(this._originalCai.fecVtoCai);
          break;
        case 'fecCreacion':
          this.fecCreacion = new Date(this._originalCai.fecCreacion);
          break;
        case 'fecModif':
          this.fecModif = this._originalCai.fecModif != null ? new Date(this._originalCai.fecModif) : null;
          break;
        default:
          // @ts-ignore
          this[key] = this._originalCai[key];
      }
    });
  }
}

@Component({
  selector: 'app-cai-maintenance',
  templateUrl: './cai-maintenance.component.html',
  styleUrls: ['./cai-maintenance.component.scss'],
})
export class CaiMaintenanceComponent implements OnInit, DeactivateGuardedComponent {

  canCreateUpdate: boolean = false;
  isLoading: boolean = true;
  caiRows: CaiRow[] = [];
  letraCodes: string[] = [];
  ptoEmisions: PtoEmision[] = [];

  displayedColumns: string[] = ['ptoEmision', 'fecDesdeCai', 'fecVtoCai', 'nroCai', 'codTipoDocImp', 'tipoDesc', 'letra', 'estado', 'usrCreacion', 'fecCreacion', 'usrModif', 'fecModif'];
  operators: FabStringFilterOperator[] = ['contains'];
  dateOperators: FabDateFilterOperator[] = ['eq', 'gte', 'lte'];
  pageSizeOptions: number[] = [10, 15, 20, 25];
  defaultPageSize: number = this.pageSizeOptions[0];
  dataSource: MatTableDataSource<CaiRow> = new MatTableDataSource<CaiRow>();

  state: FabTableState = {
    skip: 0,
    take: 10,
    filter: [],
    sort: []
  };

  public get disableSaveButton() {
    return !this.validateAllRows() ||
      this.caiRows.filter(element => element.editing).length == 0;
  }

  constructor(private readonly caiService: CaiService,
              private readonly notificationService: NotificationService,
              private readonly securityService: SecurityService,
              private readonly masterDataService: CommonService,
              private readonly ptoEmisionService: PtoEmisionService) {
  }

  ngOnInit(): void {
    this.canCreateUpdate = this.securityService.can(UserFunctionCode.CREATE_CAI);
    if (this.canCreateUpdate) {
      this.displayedColumns.push('edited');
    }
    this.fetchData();
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.dirtyCheck()) {
      return confirm("Usted tiene cambios no guardados. ¿Salir de la página de todos modos?");
    } else {
      return true;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  defaultConfirmation(event: any) {
    if (this.dirtyCheck()) {
      event.returnValue(false);
    } else {
      event.preventDefault();
    }
  }

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        default:
          // @ts-ignore
          return item[property];
      }
    };
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChildren('tableFilter') tableFilters: QueryList<RemStringFilterComponent | RemNumberFilterComponent | RemDateFilterComponent>;


  validateAllRows() {
    return this.caiRows.filter(element => element.editing).every((row) => this.validateRow(row));
  }

  validateRow(row: CaiRow) {
    return row.fecVtoCai != null &&
      row.fecDesdeCai != null &&
      row.codTipoDocImp.length > 0 &&
      row.letra.length > 0 &&
      row.ptoEmision.length > 0 &&
      row.nroCai.length > 0;
  }

  fetchData() {
    this.isLoading = true;
    forkJoin([
      this.masterDataService.getLetraList().pipe(
        tap((res) => {
          this.letraCodes = res;
        })
      ),
      this.ptoEmisionService.getAllPtoEmision().pipe(
        tap((res) => {
          this.ptoEmisions = res;
        })
      ),

      this.caiService.getAllCai().pipe(
        map((res) => {
          this.caiRows = res.map((cai) => {
            return new CaiRow(false, cai);
          });
          this.dataSource = new MatTableDataSource<CaiRow>(this.caiRows);
          this.sortTableById();
          this.processData();
        })
      )
    ]).subscribe({
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  addRow() {
    let newRow = new CaiRow(true);
    newRow.usrCreacion = this.securityService.LOGGED_USER['gmin'];
    this.caiRows.unshift(newRow);
    this.processData();
  }

  edit(row: CaiRow) {
    if (!row.editing) {
      row.usrModif = this.securityService.LOGGED_USER['gmin'];
      row.editing = true;
    }
  }

  revertRow(row: CaiRow) {
    if (row.add) {
      this.caiRows = this.caiRows.filter((item) => {
        return item !== row;
      })
    }
    row.resetEdits();
    row.editing = false;
    this.processData();
  }

  filterChange(event: any) {
    this.state.skip = 0;
    if (this.matPaginator) {
      this.matPaginator.pageIndex = 0;
    }
    // @ts-ignore
    this.state.filter = filterStateChange(event, this.state.filter);
    this.processData();
  }

  processData() {
    this.dataSource.data = process(this.caiRows, this.state);
  }

  isDirty(element: CaiRow, key: keyof Cai): boolean {
    return element[key] !== element.originalCai[key] && !element.add;
  }

  isDirtyCheck(element: CaiRow, key: keyof Cai): boolean {

    if(key == 'fecDesdeCai' || key == 'fecVtoCai'){
      return element[key].toISOString() !== new Date(element.originalCai[key]).toISOString();
    }else if(key !== 'fecCreacion' && key !== 'usrModif' && key !== 'fecModif'){
      return element[key] !== element.originalCai[key];
    }else{
      return false;
    }
  }


  private dirtyCheck(): boolean {
    return !this.caiRows.every((row) => {
      return !row.editing;
    })
  }

  sortTableById() {
    this.caiRows.sort((a, b) => {
      return b.id - a.id;
    });
  }

  saveRows() {
    if (this.validateAllRows()) {
      let observables: ObservableInput<any>[] = [];
      let errors: HttpErrorResponse[] = [];
      let saved = 0;
      this.caiRows.filter((item) => item.editing)
      .forEach((cai) => {
          const keys = Object.keys(new Cai()) as (keyof Cai)[];
          let dirty = keys.map((key) => {
            let test = this.isDirtyCheck(cai, key);
            return test;
          }).some(item => item);
          if (dirty) {
            observables.push(
              this.caiService.addUpdate(cai as Cai, cai.add)
                .pipe(
                  map((res) => {
                    cai.originalCai = res;
                    cai.editing = false;
                    cai.add = false;
                    saved++;
                  }),
                  catchError((err: HttpErrorResponse) => {
                    errors.push(err);
                    return of(null);
                  })
                )
            );
          }else{
            this.revertRow(cai);
          }
        }
      );

      forkJoin(observables).subscribe({
        next: undefined,
        error: undefined,
        complete: () => {
          if (errors.length == 0 && saved > 0) {
            this.sortTableById();
            this.processData();
            this.notificationService.open(ErrorCodes.SAVED, 'success');
          } else {
            const errorCounts = errors.map(err => err.error?.exceptionType ?? "Unknown").reduce((accumulator, value) => {
              // @ts-ignore
              return {...accumulator, [value]: (accumulator[value] || 0) + 1};
            }, {});
            let errorMessage = `${saved} línea(s) guardada(s). ${errors.length} líena(s) tiene(n) error:`
            .concat(...Object.entries(errorCounts).map(([msg, count]) => `\n${this.notificationService.getErrorByCode(msg)} (${count})`));
            this.notificationService.open(errorMessage, 'error');
          }
        }
      });

    } else {
      // invalid rows error
      this.notificationService.open(ErrorCodes.INVALID_DATA, 'error');
    }
  }

  refresh() {
    this.caiRows = [];
    this.letraCodes = [];
    this.fetchData();
  }
}
